/**
 * The onboarding wizard's progress (phase 7b item 3): a tenant admin's first
 * run through (1) a show style from the template (D32), (2) a studio, (3) a
 * server — or a cloud production — and (4) a first rundown.
 *
 * What is persisted is only what the data cannot say: which step was skipped,
 * which resource a step made (so a retry adopts it rather than making a
 * second), the choice at step 3, and whether the wizard was dismissed or
 * finished. Whether a step is DONE the SPA also reads off the tenant's own
 * lists (a show style exists…), so the wizard works — without persistence —
 * against a node, which serves no onboarding route. Cloud only, tenant-wide:
 * the first run is a tenant admin's.
 */
import { z } from 'zod';
import { isoTimestampWireSchema } from './primitives.js';

export const ONBOARDING_STEPS = ['showStyle', 'studio', 'server', 'rundown'] as const;
export const onboardingStepIdWireSchema = z.enum(ONBOARDING_STEPS);
export type OnboardingStepIdWire = z.infer<typeof onboardingStepIdWireSchema>;

export const onboardingStatusWireSchema = z.enum(['not-started', 'in-progress', 'dismissed', 'completed']);

export const onboardingStepStatusWireSchema = z.enum(['pending', 'done', 'skipped']);

/** Step 3's fork: an on-prem server, or a cloud production. */
export const onboardingChoiceWireSchema = z.enum(['server', 'cloud']);

const onboardingStepWireSchema = z
  .object({
    status: onboardingStepStatusWireSchema,
    /** The resource the step made or adopted (a show style, studio, site or rundown id); null before. */
    resourceId: z.string().min(1).max(128).nullable(),
    /** Step 3 only: which way it went; null elsewhere and before. */
    choice: onboardingChoiceWireSchema.nullable(),
    updatedAt: isoTimestampWireSchema.nullable(),
  })
  .strict();
type OnboardingStepWire = z.infer<typeof onboardingStepWireSchema>;

export const onboardingWireSchema = z
  .object({
    status: onboardingStatusWireSchema,
    /**
     * Partial (the 2026-09-27 review §1.6): an enum-keyed `z.record` is exhaustive in zod 4, so a reader whose release
     * added a step would refuse an answer from a cloud without it. A step absent here is not yet done; the cloud fills
     * the steps it knows (`withEveryStep`), and a reader's tolerant parse drops one it does not.
     */
    steps: z.partialRecord(onboardingStepIdWireSchema, onboardingStepWireSchema),
    updatedAt: isoTimestampWireSchema.nullable(),
    updatedBy: z.string().max(320).nullable(),
  })
  .strict();
export type OnboardingWire = z.infer<typeof onboardingWireSchema>;

export const onboardingResponseWireSchema = z.object({ onboarding: onboardingWireSchema }).strict();

/**
 * One change: the wizard's status, one step's, or both. Idempotent — saying the same thing twice changes nothing
 * — and a step's `resourceId`, once set, is kept unless a new one is named.
 */
export const updateOnboardingRequestWireSchema = z
  .object({
    status: onboardingStatusWireSchema.exclude(['not-started']).optional(),
    step: z
      .object({
        id: onboardingStepIdWireSchema,
        status: onboardingStepStatusWireSchema,
        resourceId: z.string().regex(/^[A-Za-z0-9_-]{1,128}$/).nullable().optional(),
        choice: onboardingChoiceWireSchema.nullable().optional(),
      })
      .strict()
      .optional(),
  })
  .strict()
  .refine((body) => body.status !== undefined || body.step !== undefined, { message: 'name a status, a step, or both' });
export type UpdateOnboardingRequestWire = z.infer<typeof updateOnboardingRequestWireSchema>;

/** A tenant's progress before anything was saved. */
export function initialOnboarding(): OnboardingWire {
  const pending: OnboardingStepWire = { status: 'pending', resourceId: null, choice: null, updatedAt: null };
  return { status: 'not-started', steps: { showStyle: pending, studio: pending, server: pending, rundown: pending }, updatedAt: null, updatedBy: null };
}
